import { useCallback } from 'react'

import { sendReportPdfToOIC } from '../../../api/oracleIntegration'
import { ProjectDocument } from '../../../api/resources/project'
import iconReportDelete from '../../../assets/images/icon_report_delete.svg'
import iconReportDownload from '../../../assets/images/icon_report_download.svg'
import iconReportPrint from '../../../assets/images/icon_report_print.svg'
import iconReportShare from '../../../assets/images/icon_report_send.svg'
import { useBuildingContext } from '../../../contexts/buildingContext'
import { useNotifications } from '../../../contexts/notificationProvider'
import { projectHasUnifierIntegration } from '../../../utils/unifierIntegration'
import { SendToUnifierButton } from '../../common/OracleIntegration'
import { StandardButton } from '../../common/StandardButton'
import { MainPanel } from '../../layout/main-panel/MainPanel'

interface ReportsActionSlotProps {
  document?: ProjectDocument | null
  deleting?: boolean
  allowDelete?: boolean
  onDelete?: () => void
}

export const ReportsActionSlot = ({
  document,
  deleting = false,
  allowDelete = false,
  onDelete,
}: ReportsActionSlotProps) => {
  const { addNotification } = useNotifications()
  const { state: buildingState } = useBuildingContext()

  const handlePrint = useCallback(() => {
    if (document?.document_s3) {
      const printWindow = window.open(
        document.document_s3,
        '_blank',
        'noopener,noreferrer',
      )
      if (printWindow) {
        const interval = setInterval(() => {
          if (printWindow.document.readyState === 'complete') {
            clearInterval(interval)
            printWindow.print()
          }
        }, 500) // Check every 500ms if the document is fully loaded
      } else {
        // eslint-disable-next-line no-console -- TODO: Replace with proper error logging
        console.error(
          'Failed to open the print window. It might be blocked by the browser.',
        )
      }
    }
  }, [document])

  const handleDownload = useCallback(async () => {
    if (document?.document_s3) {
      try {
        const response = await fetch(document.document_s3)
        const blob = await response.blob()
        const link = window.document.createElement('a')
        link.href = window.URL.createObjectURL(blob)
        const base = document.name || 'report'
        link.download = base.toLowerCase().endsWith('.pdf')
          ? base
          : `${base}.pdf`
        window.document.body.appendChild(link)
        link.click()
        window.document.body.removeChild(link)
      } catch (error) {
        // eslint-disable-next-line no-console -- TODO: Replace with proper error logging
        console.error('Failed to download the document:', error)
      }
    }
  }, [document])

  const handleShare = useCallback(() => {
    const currentUrl = window.location.href
    navigator.clipboard
      .writeText(currentUrl)
      .then(() => {
        addNotification('Page link copied to clipboard!', 'success')
      })
      .catch(error => {
        // eslint-disable-next-line no-console -- TODO: Replace with proper error logging
        console.error('Failed to copy the page link:', error)
        addNotification('Failed to copy the link.', 'error')
      })
  }, [addNotification])

  return (
    <MainPanel.Slot>
      {document?.date && (
        <MainPanel.SlotLabel muted>
          {new Date(document.date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })}
        </MainPanel.SlotLabel>
      )}
      <MainPanel.SlotAction
        icon={iconReportShare}
        disabled={!document}
        onTrigger={handleShare}
      />
      <MainPanel.SlotAction
        icon={iconReportPrint}
        disabled={!document}
        onTrigger={handlePrint}
      />
      <MainPanel.SlotAction
        icon={iconReportDownload}
        disabled={!document}
        onTrigger={handleDownload}
      />
      {allowDelete && (
        <StandardButton
          icon={iconReportDelete}
          disabled={!document || deleting}
          style={{
            height: 36,
            minHeight: 36,
            padding: '6px 12px',
            flexShrink: 0,
          }}
          onClick={() => {
            if (!deleting) onDelete?.()
          }}
        >
          Delete
        </StandardButton>
      )}
      {document?.id && (
        <SendToUnifierButton
          entityLabel="Report PDF"
          unifierIntegrationActive={projectHasUnifierIntegration(
            buildingState.projectData,
          )}
          onSend={async () => {
            await sendReportPdfToOIC(
              buildingState.projectId,
              document.id,
              window.location.href,
            )
          }}
          stylePreset="reportsToolbar"
        />
      )}
    </MainPanel.Slot>
  )
}
